import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

/**
 * A CSV column reference: one header, or ordered candidates from which the parser
 * picks the first present non-empty value (tolerates multiple export shapes).
 */
export type ColumnRef = string | string[];

export interface ImportMapping {
  provider: string;
  shopifyCustomerId?: ColumnRef;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  email?: string;
  phone?: string;
  tags?: string;
  acceptsEmailMarketing?: string;
  emailMarketingTrueValue?: string;
  emailMarketingUnsubscribedValue?: string;
  emailMarketingSuppressed?: string;
  // Opt-out held in a column separate from subscribe status (e.g. Attentive
  // "... Opted Out"); when it matches, unsubscribe wins over the status column.
  emailMarketingOptedOut?: string;
  emailMarketingOptedOutTrueValue?: string;
  acceptsSmsMarketing?: string;
  smsMarketingTrueValue?: string;
  smsMarketingUnsubscribedValue?: string;
  smsMarketingOptedOut?: string;
  smsMarketingOptedOutTrueValue?: string;
  address?: {
    street1?: string;
    street2?: string;
    city?: string;
    state?: string;
    zip?: string;
    country?: ColumnRef;
  };
  birthMonth?: string;
  birthday?: string;
  customFields?: Record<string, string>;
}

export enum ImportProvider {
  SHOPIFY = "shopify",
  KLAVIYO = "klaviyo",
  ATTENTIVE = "attentive",
  CUSTOM = "custom",
}

/**
 * Hard cap on subscriber-import CSV size, enforced server-side against the
 * uploaded S3 object (see queueCustomerImports) to bound import-processing cost.
 */
export const MAX_IMPORT_FILE_SIZE_BYTES = 500 * 1024 * 1024;

export const importProviderMapping: Record<ImportProvider, ImportMapping> = {
  [ImportProvider.SHOPIFY]: {
    provider: ImportProvider.SHOPIFY,
    shopifyCustomerId: ["Customer ID", "ID"],
    firstName: "First Name",
    lastName: "Last Name",
    email: "Email",
    phone: "Phone",
    tags: "Tags",
    acceptsEmailMarketing: "Accepts Email Marketing",
    emailMarketingTrueValue: "yes",
    emailMarketingUnsubscribedValue: "no",
    acceptsSmsMarketing: "Accepts SMS Marketing",
    smsMarketingTrueValue: "yes",
    smsMarketingUnsubscribedValue: "no",
    address: {
      street1: "Default Address Address1",
      street2: "Default Address Address2",
      city: "Default Address City",
      state: "Default Address Province Code",
      zip: "Default Address Zip",
      country: ["Default Address Country Code", "Default Address Country"],
    },
  },
  [ImportProvider.KLAVIYO]: {
    provider: ImportProvider.KLAVIYO,
    firstName: "First Name",
    lastName: "Last Name",
    email: "Email",
    phone: "Phone Number",
    acceptsEmailMarketing: "Email Marketing Consent",
    emailMarketingTrueValue: "SUBSCRIBED",
    emailMarketingUnsubscribedValue: "UNSUBSCRIBED",
    emailMarketingSuppressed: "Email Suppressions",
    acceptsSmsMarketing: "SMS Marketing Consent",
    smsMarketingTrueValue: "SUBSCRIBED",
    smsMarketingUnsubscribedValue: "UNSUBSCRIBED",
    address: {
      street1: "Address",
      street2: "Address 2",
      city: "City",
      state: "State / Region",
      zip: "Zip Code",
      country: "Country",
    },
  },
  [ImportProvider.ATTENTIVE]: {
    provider: ImportProvider.ATTENTIVE,
    firstName: "First Name",
    lastName: "Last Name",
    email: "Subscriber Email",
    phone: "Subscriber Phone Number",
    acceptsSmsMarketing: "SMS Marketing Status",
    smsMarketingTrueValue: "ACTIVE",
    smsMarketingOptedOut: "SMS Marketing Opted Out",
    smsMarketingOptedOutTrueValue: "TRUE",
    acceptsEmailMarketing: "Email Marketing Status",
    emailMarketingTrueValue: "ACTIVE",
    emailMarketingOptedOut: "Email Marketing Opted Out",
    emailMarketingOptedOutTrueValue: "TRUE",
  },
  [ImportProvider.CUSTOM]: {
    provider: ImportProvider.CUSTOM,
    email: "Email",
    firstName: "First Name",
    lastName: "Last Name",
    fullName: "Full Name",
    phone: "Phone",
    tags: "Tags",
    acceptsEmailMarketing: "Email Marketing Subscribed",
    emailMarketingTrueValue: "TRUE",
    emailMarketingUnsubscribedValue: "FALSE",
    acceptsSmsMarketing: "SMS Marketing Subscribed",
    smsMarketingTrueValue: "TRUE",
    smsMarketingUnsubscribedValue: "FALSE",
    address: {
      street1: "Address",
      street2: "Address 2",
      city: "City",
      state: "State",
      zip: "Zip Code",
      country: "Country",
    },
    birthMonth: "Birth Month",
    birthday: "Birthday",
  },
};

export enum WorkflowError {
  FAILED = "Workflow execution failed",
  CANCELED = "Customer import cancelled",
}

const CustomerImportProgressSchema_ = z.object({
  importId: z.string(),
  segmentName: z.string().optional(),
  error: createEnum(WorkflowError).optional(),
  provider: createEnum(ImportProvider).optional(),
  lastRow: z.number(),
  totalRows: z.number(),
  progress: z.number(),
  totalCustomersImported: z.number(),
  totalCustomersSkipped: z.number(),
  totalEmailSubscribers: z.number(),
  totalSmsSubscribers: z.number(),
  totalFailed: z.number(),
});

export const CustomerImportProgressSchema: z.ZodType<CustomerImportProgress> =
  CustomerImportProgressSchema_;
export interface CustomerImportProgress extends z.infer<
  typeof CustomerImportProgressSchema_
> {}
